import { Injectable } from '@angular/core';
import {
  HttpEvent,
  HttpInterceptor,
  HttpHandler,
  HttpRequest,
} from '@angular/common/http';
import { NEVER, Observable, from, of } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { BasicDialogComponent } from '../shared/components/basic-dialog/basic-dialog.component';
import config from '../../config';
import { fetchAuthSession } from 'aws-amplify/auth';
import { SimpleDialog } from '../shared/models/simple-dialog';

@Injectable()
export class TokenInterceptor implements HttpInterceptor {
  constructor(private dialog: MatDialog, public router: Router) {}

  intercept(
    req: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    const originalUrl = req.url;
    const simpleDialog: SimpleDialog = {
      uiTitle: 'Your session has expired',
      uiMessage:
        "To continue, please click 'OK' to be redirected to the login page.",
      redirect: '/auth/logout',
    };

    // Skip token processing for non-API requests
    if (!originalUrl.includes(`${config.API_GATEWAY_ENDPOINT}`)) {
      return next.handle(req);
    }

    // Convert Promise to Observable and use switchMap to wait for completion.
    // catchError here only guards the fetchAuthSession() call - it must NOT
    // wrap next.handle(), otherwise real HTTP errors from the request itself
    // (e.g. 404/401 from the API) would be misreported as auth-session errors
    // and silently retried without the Authorization header.
    return from(fetchAuthSession()).pipe(
      catchError((error) => {
        console.error('Error fetching auth session:', error);

        // If it's a login request or other auth endpoint, allow without token
        if (originalUrl.includes('/auth/') || originalUrl.includes('/login')) {
          console.log('Auth endpoint request, proceeding without token');
        } else {
          console.warn('Proceeding with request without authentication');
        }
        return of(null);
      }),
      switchMap((session) => {
        if (!session) {
          return next.handle(req);
        }

        const accessToken = session.tokens?.accessToken?.toString();
        const idToken = session.tokens?.idToken;
        const tokenExpirationTime = idToken?.payload.exp;
        const now = Math.floor(Date.now() / 1000);

        // Check for token expiration
        if (tokenExpirationTime && tokenExpirationTime <= now) {
          console.log('Token expired, showing dialog');
          this.dialog.open(BasicDialogComponent, { data: simpleDialog });
          return NEVER;
        }

        // Add the token to the request
        if (accessToken) {
          const modifiedReq = req.clone({
            headers: req.headers.set('Authorization', accessToken),
          });
          return next.handle(modifiedReq);
        } else {
          console.warn('No access token available, proceeding without token');
          this.dialog.open(BasicDialogComponent, { data: simpleDialog });
          return next.handle(req);
        }
      })
    );
  }
}
